/**
 * Promotion Request Form Component
 * Allows workers to request promotion to Team Leader
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Send, Clock, CheckCircle2, XCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PromotionRequestForm({ onSuccess }) {
  const [reason, setReason] = useState("");
  const [experienceYears, setExperienceYears] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [existingRequest, setExistingRequest] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkExistingRequest();
  }, []);

  const checkExistingRequest = async () => {
    try {
      const res = await axios.get(`${API}/promotion-requests/my`);
      setExistingRequest(res.data);
    } catch (error) {
      // No existing request
      setExistingRequest(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/promotion-requests`, {
        requested_role: "team_leader",
        reason: reason,
        experience_years: parseInt(experienceYears) || 0
      });
      onSuccess();
      checkExistingRequest(); // Refresh to show the new request
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // Show existing request status
  if (existingRequest) {
    const statusConfig = {
      pending: { icon: <Clock className="w-6 h-6 text-yellow-400" />, color: "yellow", text: "Pending Review" },
      approved: { icon: <CheckCircle2 className="w-6 h-6 text-green-400" />, color: "green", text: "Approved!" },
      rejected: { icon: <XCircle className="w-6 h-6 text-red-400" />, color: "red", text: "Not Approved" }
    };
    const status = statusConfig[existingRequest.status] || statusConfig.pending;

    return (
      <div className="space-y-4">
        <div className={`p-6 bg-${status.color}-500/10 rounded-lg border border-${status.color}-500/30`}>
          <div className="flex items-center gap-3 mb-4">
            {status.icon}
            <div>
              <p className={`text-${status.color}-400 font-bold text-lg`}>{status.text}</p>
              <p className="text-[#94A3B8] text-sm">
                Submitted: {new Date(existingRequest.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>
          
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-[#94A3B8]">Experience:</span>
              <span className="text-white">{existingRequest.experience_years} years</span>
            </div>
            <div>
              <span className="text-[#94A3B8]">Your message:</span>
              <p className="text-white mt-1 p-2 bg-black/30 rounded">{existingRequest.reason}</p>
            </div>
          </div>

          {existingRequest.status === 'pending' && (
            <p className="text-yellow-400 text-sm mt-4 flex items-center gap-2">
              <Clock className="w-4 h-4" />
              Your request is being reviewed by the admin team
            </p>
          )}
          
          {existingRequest.status === 'rejected' && existingRequest.admin_notes && (
            <div className="mt-4 p-3 bg-red-500/10 rounded border border-red-500/30">
              <p className="text-red-400 text-sm font-medium">Admin feedback:</p>
              <p className="text-[#94A3B8] text-sm">{existingRequest.admin_notes}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label className="text-[#94A3B8]">Years of Experience in Scaffolding</Label>
        <Input
          type="number"
          value={experienceYears}
          onChange={(e) => setExperienceYears(e.target.value)}
          placeholder="e.g., 5"
          min="0"
          className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-[#94A3B8]">Why do you want to become a Team Leader?</Label>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Describe your experience, leadership skills, and why you're ready for this role..."
          rows={4}
          className="bg-black/50 border-white/10 text-white focus:border-[#23F7DD]"
        />
      </div>
      <Button
        onClick={handleSubmit}
        disabled={submitting}
        className="w-full bg-purple-500 text-white hover:bg-purple-600"
      >
        {submitting ? (
          <span className="flex items-center gap-2">
            <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
            Submitting...
          </span>
        ) : (
          <>
            <Send className="w-4 h-4 mr-2" />
            Submit Request
          </>
        )}
      </Button>
    </div>
  );
}
